import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Zap, Plus, TrendingUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const creditPackages = [];

const AXIS_TICK_STYLE = { fill: 'rgba(255,255,255,0.4)', fontSize: 12 };
const TOOLTIP_STYLE = {
  backgroundColor: '#0A1222',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: 'white'
};

export default function UsagePage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/usage/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (err) { logger.error("Operation failed:", err); } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const creditsUsed = user?.credits_total ? user.credits_total - user.credits_remaining : 0;
  const creditsPercentage = user?.credits_total ? (creditsUsed / user.credits_total) * 100 : 0;

  // Transform daily usage for chart
  const chartData = Object.entries(stats?.daily_usage || {})
    .slice(-14)
    .map(([date, credits]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      credits
    }));

  const usageByFeature = [
    { name: 'AI Rewrites', credits: stats?.action_stats?.rewrite?.credits || 0, count: stats?.action_stats?.rewrite?.count || 0 },
    { name: 'Grammar Check', credits: stats?.action_stats?.grammar?.credits || 0, count: stats?.action_stats?.grammar?.count || 0 },
    { name: 'PI Craft', credits: stats?.action_stats?.prompt?.credits || 0, count: stats?.action_stats?.prompt?.count || 0 },
    { name: 'Content Generator', credits: stats?.action_stats?.generate?.credits || 0, count: stats?.action_stats?.generate?.count || 0 },
    { name: 'Emotion Analysis', credits: stats?.action_stats?.emotion?.credits || 0, count: stats?.action_stats?.emotion?.count || 0 },
  ];

  const handleBuyCredits = () => {
    alert('Credit purchase packs will be available after the pilot. During the pilot, credits reset monthly for free.');
    setBuyDialogOpen(false);
  };

  return (
    <div className="space-y-8" data-testid="usage-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Usage & Credits</h1>
        <p className="text-white/55">Track your SendPI usage and manage credits</p>
      </div>

      {/* Credit Balance */}
      <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-white/5 rounded-2xl p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white/55 text-sm">Credits Remaining</p>
                <p className="text-white font-space text-3xl">{user?.credits_remaining?.toLocaleString() || 0}</p>
              </div>
            </div>
            <div className="space-y-2">
              <Progress value={creditsPercentage} className="h-3 bg-white/10" />
              <div className="flex justify-between text-sm">
                <span className="text-white/40">{Math.round(creditsPercentage)}% used</span>
                <span className="text-white/55">Resets in 8 days</span>
              </div>
            </div>
          </div>

          <Dialog open={buyDialogOpen} onOpenChange={setBuyDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white rounded-full" data-testid="buy-credits-btn">
                <Plus className="w-4 h-4 mr-2" />
                Buy more credits
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0E1830] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Credits</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <p className="text-white/60 text-sm">During the pilot, credits reset monthly for free. Credit purchase packs will be available after launch.</p>
                <p className="text-white/40 text-xs text-center">
                  Credits never expire and stack with your monthly allowance.
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Usage Chart */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-medium mb-6">Usage (Last 14 Days)</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis 
                dataKey="date" 
                axisLine={false} 
                tickLine={false} 
                tick={AXIS_TICK_STYLE}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={AXIS_TICK_STYLE}
              />
              <Tooltip 
                contentStyle={TOOLTIP_STYLE}
              />
              <Bar dataKey="credits" radius={[4, 4, 0, 0]}>
                {chartData.map((entry) => (
                  <Cell key={entry.date} fill="url(#gradient)" />
                ))}
              </Bar>
              <defs>
                <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38BDF8" />
                  <stop offset="100%" stopColor="#818CF8" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Usage Breakdown */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-medium mb-6">Credit Consumption by Feature</h3>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="usage-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/55 font-medium text-sm">Feature</th>
                <th className="text-right py-3 px-4 text-white/55 font-medium text-sm">Times Used</th>
                <th className="text-right py-3 px-4 text-white/55 font-medium text-sm">Credits Used</th>
              </tr>
            </thead>
            <tbody>
              {usageByFeature.map((item) => (
                <tr key={item.name} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-3 px-4 text-white">{item.name}</td>
                  <td className="py-3 px-4 text-white/70 text-right">{item.count}</td>
                  <td className="py-3 px-4 text-white text-right">{item.credits.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
